import {
	CHECKOUT_SYSTEM_FIELDS,
	CheckoutStatusFilter,
	type CustomField,
	getFlagColor,
	ResourceType,
} from '@cheqroom/api';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import type { FilterFieldEntry } from './types';
import { getDynamicFilterEntries } from './utils/get-dynamic-filter-entries';

const EMPTY_HIDE_FILTERS: string[] = [];

const getCheckoutStatusOptions = (t: TFunction<'checkout-info'>) => [
	{ label: t('checkout-info:status.draft'), value: CheckoutStatusFilter.DRAFT },
	{ label: t('checkout-info:status.open'), value: CheckoutStatusFilter.OPEN },
	{ label: t('checkout-info:status.overdue'), value: CheckoutStatusFilter.OVERDUE },
	{ label: t('checkout-info:status.closed'), value: CheckoutStatusFilter.CLOSED },
	{ label: t('checkout-info:status.archived'), value: CheckoutStatusFilter.ARCHIVED },
];

const getMaintenanceOptions = (t: TFunction<'booking-filters'>, resourceType: ResourceType) => [
	{ label: t('booking-filters:maintenance.all'), value: 'all' },
	{ label: t('booking-filters:maintenance.include', { resourceType }), value: 'maintenance' },
	{ label: t('booking-filters:maintenance.exclude', { resourceType }), value: 'no_maintenance' },
];

type OrderLabel = { id: string; name: string; color: string };

export const UNLABELED_CHECKOUT_LABEL_ID = 'unlabeled';

const getOrderLabelOptions = (t: TFunction<'common'>, labels: OrderLabel[]) => [
	{
		label: t('common:filter.labels.unlabeled'),
		value: UNLABELED_CHECKOUT_LABEL_ID,
		color: getFlagColor('SLATE_GRAY'),
		icon: 'RoundedSquare',
	},
	...labels.map((label) => ({
		label: label.name,
		value: label.id,
		color: getFlagColor(label.color),
		icon: 'RoundedSquare',
	})),
];

interface UseCheckoutFiltersProps {
	t: TFunction<'checkout-info' | 'booking-filters' | 'common'>;
	customFields?: CustomField[];
	orderLabels?: OrderLabel[];
	hideFilters?: string[];
}

export const useCheckoutFilters = ({
	t,
	customFields,
	orderLabels,
	hideFilters = EMPTY_HIDE_FILTERS,
}: UseCheckoutFiltersProps) => {
	const filters = useMemo<FilterFieldEntry[]>(() => {
		const allStaticFilters: FilterFieldEntry[] = [
			{
				id: 'checkoutStatus',
				name: t('filter.status.trigger'),
				type: 'select',
				options: getCheckoutStatusOptions(t),
			},
			{
				id: 'maintenance',
				name: t('booking-filters:maintenance.trigger'),
				type: 'select',
				selectMode: 'single',
				options: getMaintenanceOptions(t, ResourceType.CHECKOUTS),
			},
			{ id: 'date_range', name: t('filter.date.trigger_dates'), type: 'dateRange' },
			{ id: 'locations', name: t('filter.location.trigger') },
			{
				id: 'labels',
				name: t('filter.labels.trigger'),
				type: 'select',
				options: getOrderLabelOptions(t, orderLabels ?? []),
			},
			{ id: 'users', name: t('filter.user.trigger'), type: 'customer' },
		];
		const staticFilters = allStaticFilters.filter((entry) => !hideFilters.includes(entry.id));

		const dynamicFilters = getDynamicFilterEntries([
			...CHECKOUT_SYSTEM_FIELDS,
			...(customFields ?? []).map((field) => ({ ...field, id: `fields.${field.name}` })),
		]);

		return [...staticFilters, ...dynamicFilters];
	}, [t, customFields, orderLabels, hideFilters]);

	return {
		filters,
	};
};
